/**
 * WelcomeStep — первый экран онбординга.
 *
 * Hero-сцена: лунная ночь + 3D-надпись LUNA + подзаголовок +
 * одна крупная CTA-кнопка "Начать путешествие".
 *
 * Ассеты:
 *   - /onboarding/hero_moon_scene.webp (1024×1536, гпт-image-1.5)
 *   - /onboarding/luna_logo_3d.webp (square glass typography)
 *
 * Анимация:
 *   - Hero fade+scale 0.96→1 вход 900мс
 *   - Logo shared layoutId="luna-logo" (для переходов)
 *   - Подзаголовок stagger 80мс
 *   - CTA fade+slide-up через 400мс
 */
import { useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { SPRING_SCREEN, SPRING_CARD } from '../springs';
import { useSounds } from '../useSounds';

interface WelcomeStepProps {
  onNext: () => void;
}

export function WelcomeStep({ onNext }: WelcomeStepProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();

  // Звук появления — shimmer при монтировании
  useEffect(() => {
    const t = window.setTimeout(() => sounds.shimmer(), 120);
    return () => window.clearTimeout(t);
  }, [sounds]);

  return (
    <div className="relative w-full h-full flex flex-col items-center px-6">
      {/* Полноэкранный композит LUNA + PRO₮OKOL (tc2) — он сам содержит
          лунный фон и надпись, поэтому отдельного hero-moon и отдельного
          logo-блока больше нет. */}
      <motion.img
        layoutId="luna-logo"
        src="/onboarding/luna_logo_3d.webp"
        alt="LUNA PROTOKOL"
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ ...SPRING_SCREEN, delay: 0.1 }}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
      />

      {/* Мягкая тёмная подложка снизу — чтобы CTA-блок читался поверх. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'linear-gradient(180deg, rgba(6,8,18,0.0) 0%, rgba(6,8,18,0.0) 55%, rgba(6,8,18,0.88) 100%)',
        }}
      />

      {/* Спейсер — отодвигаем блок с кнопкой вниз */}
      <div className="flex-1" />

      {/* Нижний блок: заголовок с glass-подложкой + CTA, поднят выше */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.75 }}
        className="relative z-10 w-full max-w-[360px] flex flex-col items-center gap-5 mb-[14vh]"
      >
        <div
          className="w-full px-5 py-4 rounded-[22px] text-center"
          style={{
            background: 'rgba(10,14,28,0.62)',
            backdropFilter: 'blur(18px) saturate(160%)',
            WebkitBackdropFilter: 'blur(18px) saturate(160%)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow:
              '0 16px 44px -16px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.14)',
          }}
        >
          <p
            className="text-[19px] leading-tight font-bold"
            style={{
              color: 'rgba(245,250,255,0.98)',
              letterSpacing: '-0.015em',
            }}
          >
            {t('onboarding.welcome.title')}
          </p>
          <p
            className="text-[13px] mt-1"
            style={{ color: 'rgba(210,220,240,0.78)' }}
          >
            {t('onboarding.welcome.subtitle')}
          </p>
        </div>

        <motion.button
          type="button"
          onClick={() => {
            haptic.medium();
            sounds.swipe();
            onNext();
          }}
          whileTap={{ scale: 0.97 }}
          transition={SPRING_CARD}
          className="w-full px-8 py-4 rounded-full font-semibold select-none"
          style={{
            fontSize: 15,
            letterSpacing: '0.02em',
            color: '#06091c',
            background: 'linear-gradient(180deg, #f5e6a8 0%, #e9c76a 100%)',
            border: '1px solid rgba(255,230,150,0.5)',
            boxShadow:
              '0 14px 34px -10px rgba(234,196,96,0.6), inset 0 1px 0 rgba(255,255,255,0.5)',
            WebkitTapHighlightColor: 'transparent',
            cursor: 'pointer',
          }}
        >
          {t('onboarding.welcome.cta')}
        </motion.button>
      </motion.div>
    </div>
  );
}
